"use client";
/* The hero's code card. Signed in, it calls GET /api/creators/me:
   - a creator sees her own code and live stats: referrals, boxes they've
     opened, credit earned, current tier and next tier;
   - 404 NOT_A_CREATOR shows the API's message ("You don't have a creator
     code yet") in the "Your code" spot, with dashes for the stats.
   Signed out, still loading, or any other failure: the sample card, which
   is decoration only. "Next free box at" is static for now. */
import { useEffect, useState } from "react";
import { useAuth } from "@/components/providers/AuthProvider";
import { ApiError } from "@/lib/client/api";
import { getMyCreator, type MyCreator } from "@/lib/client/creators";
import { moneyFromCents } from "@/lib/catalog/logic";

export const NEXT_FREE_BOX_AT = 50;

const SAMPLE = { code: "MIA5", referrals: "38", boxesOpened: "214", earned: "$58", tier: "It Girl", nextTier: "Icon" };
export const NO_CODE_MESSAGE = "You don't have a creator code yet";

export function CreatorStatsCard() {
  const auth = useAuth();
  const userId = auth.status === "signedIn" ? auth.user?.id ?? "" : "";
  const code = auth.status === "signedIn" ? auth.user?.creatorCode || "" : "";
  const [mine, setMine] = useState<MyCreator | null>(null);
  const [noCode, setNoCode] = useState(""); /* the API's NOT_A_CREATOR message */

  /* again when she signs in or out, or her code changes (e.g. right after a claim) */
  useEffect(() => {
    setMine(null);
    setNoCode("");
    if (!userId) return;
    let live = true;
    getMyCreator().then(
      (m) => { if (live) setMine(m); },
      (e) => { if (live && e instanceof ApiError && e.code === "NOT_A_CREATOR") setNoCode(e.message || NO_CODE_MESSAGE); /* anything else: the sample card */ },
    );
    return () => { live = false; };
  }, [userId, code]);

  const n = (x: number) => x.toLocaleString("en-US");
  const v = mine
    ? { code: mine.code, referrals: n(mine.referrals), boxesOpened: n(mine.boxesOpened), earned: moneyFromCents(mine.earnedCents), tier: mine.tier.name, nextTier: mine.nextTier?.name ?? "Top tier" }
    : noCode
      ? { code: noCode, referrals: "—", boxesOpened: "—", earned: "—", tier: "—", nextTier: "—" }
      : SAMPLE;
  const live = !!(mine || noCode);

  return (
    <div className="code-card" data-live={live ? "true" : undefined} aria-hidden={live ? undefined : true}>
      <div className="cc-label">Your code</div>
      <div className={noCode ? "cc-code cc-none" : "cc-code"}>{v.code}</div>
      <div className="cc-row"><span>Your referrals</span><b>{v.referrals}</b></div>
      <div className="cc-row"><span>Boxes they&apos;ve opened</span><b>{v.boxesOpened}</b></div>
      <div className="cc-row"><span>Credit earned</span><b>{v.earned}</b></div>
      <div className="cc-row"><span>Next free box at</span><b>{NEXT_FREE_BOX_AT}</b></div>
      <div className="cc-row"><span>Current tier</span><b>{v.tier}</b></div>
      <div className="cc-row"><span>Next tier</span><b>{v.nextTier}</b></div>
    </div>
  );
}
